import { useCallback, useEffect, useState, ReactNode } from 'react'

import {
  useSuggestionsQuery,
  useSuggestionsLazyQuery,
  useGetBucketListItemsIndexQuery,
} from '../../hooks/useQueries'

import StoryCard from '../../components/cards/StoryCard'
import ListingCard from '../../components/cards/ListingCard'
import WithSelectEscape from '../../components/SelectEscape/WithSelectEscape'
import TrackVisibility from '../../components/utils/TrackVisibility'
import PaginatedSlides from '../../components/display/PaginatedSlides'
import { Title } from '../../components/display/PaginatedSlides/shared'

type UseScopedEscapesArgs = {
  canFetchMoreRegional: boolean
  regionalSuggestionsCount?: number
  loadMoreEscapesInMarket: () => any
}

const useScopedEscapes = ({
  canFetchMoreRegional,
  regionalSuggestionsCount,
  loadMoreEscapesInMarket,
}: UseScopedEscapesArgs) => {
  const [hasEnoughRegionalEscapes, setHasEnoughRegionalEscapes] = useState(true)

  useEffect(() => {
    if (canFetchMoreRegional === false && regionalSuggestionsCount < 3) {
      setHasEnoughRegionalEscapes(false)
    }
  }, [canFetchMoreRegional, regionalSuggestionsCount, hasEnoughRegionalEscapes])

  useEffect(() => {
    if (hasEnoughRegionalEscapes === false) {
      loadMoreEscapesInMarket()
    }
  }, [hasEnoughRegionalEscapes])

  return hasEnoughRegionalEscapes
}

type StorySuggestion = GQL_StoryCardFragmentFragment & {
  featuredListing: GQL_StoryFeaturedListingFragmentFragment
}
type ListingSuggestion = GQL_ListingCardFragmentFragment
type Suggestion = StorySuggestion | ListingSuggestion

const SuggestionItem = ({ item }: { item: Suggestion }) => {
  const { data: bucketListItemsIndexQueryData } =
    useGetBucketListItemsIndexQuery()

  const bucketListItemsIndex =
    bucketListItemsIndexQueryData?.currentUser?.bucketListItemsIndex

  // eslint-disable-next-line react/destructuring-assignment
  switch (item.__typename) {
    case 'Story': {
      const { id, featuredListing } = item

      return (
        <WithSelectEscape
          itemId={id}
          itemType="Story"
          bucketListItemsIndex={bucketListItemsIndex}
        >
          <StoryCard story={item} listing={featuredListing} portrait={false} />
        </WithSelectEscape>
      )
    }
    case 'Listing': {
      const { id } = item

      return (
        <WithSelectEscape
          itemId={id}
          itemType="Listing"
          bucketListItemsIndex={bucketListItemsIndex}
        >
          <ListingCard listing={item} />
        </WithSelectEscape>
      )
    }
    default:
      return null
  }
}

type SuggestionsProps = {
  listing: {
    id: string
    state: string
    region_name: string
    region: {
      slug: string
    }
    market: {
      name: string
      code: string
    }
  }
  customTitle?: ReactNode
  // When true (listing page only) the region query is SSR-seeded: it runs
  // non-lazily and reads the seed on the server + first client render so the
  // cards land in the server HTML. Other callers (story/adventure) leave it
  // false and keep the lazy, scroll-triggered, hydration-gated behaviour.
  prefetched?: boolean
}

const Suggestions = ({
  listing,
  customTitle,
  prefetched = false,
}: SuggestionsProps) => {
  // Non-prefetched callers defer one tick so the first client render matches the
  // server (which renders null), avoiding a React 18 hydration mismatch.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])
  const [visible, setVisible] = useState(false)

  // Prefetched path: non-lazy + cache-first so the query reads the SSR seed on
  // the server and the first client render with loading=false (cache-and-network
  // would set loading=true on the client and trip a hydration mismatch).
  const prefetchedRegion = useSuggestionsQuery({
    variables: { listingId: listing.id, scope: 'region' },
    fetchPolicy: 'cache-first',
    skip: !prefetched,
  })

  // Non-prefetched path: lazy, fired when the section scrolls into view.
  const [
    loadMoreEscapesInRegion,
    {
      called: lazyRegionCalled,
      loading: lazyRegionLoading,
      data: lazyRegionData,
      fetchMore: lazyFetchMoreRegion,
    },
  ] = useSuggestionsLazyQuery({
    fetchPolicy: 'cache-and-network',
  })

  const moreEscapesRegionCalled = prefetched ? true : lazyRegionCalled
  const moreEscapesRegionLoading = prefetched
    ? prefetchedRegion.loading
    : lazyRegionLoading
  const moreEscapesRegionData = prefetched
    ? prefetchedRegion.data
    : lazyRegionData
  const fetchMoreRegion = prefetched
    ? prefetchedRegion.fetchMore
    : lazyFetchMoreRegion

  const regionalSuggestions = moreEscapesRegionData?.moreEscapes?.edges?.map(
    ({ node }) => node.result,
  ) as Suggestion[]

  const regionPageInfo = moreEscapesRegionData?.moreEscapes?.pageInfo

  const canFetchMoreRegional = regionPageInfo?.hasNextPage

  useEffect(() => {
    if (!prefetched && visible) {
      loadMoreEscapesInRegion({
        variables: {
          listingId: listing.id,
          scope: 'region',
        },
      })
    }
  }, [prefetched, visible, listing.id])

  const handleFetchMoreRegional = useCallback(() => {
    if (moreEscapesRegionCalled && !moreEscapesRegionLoading) {
      fetchMoreRegion({
        variables: {
          cursor: regionPageInfo?.endCursor,
        },
      })
    }
  }, [
    fetchMoreRegion,
    moreEscapesRegionCalled,
    moreEscapesRegionLoading,
    regionPageInfo,
  ])

  const [
    loadMoreEscapesInMarket,
    {
      called: moreEscapesMarketCalled,
      loading: moreEscapesMarketLoading,
      data: moreEscapesMarketData,
      fetchMore: fetchMoreMarket,
    },
  ] = useSuggestionsLazyQuery({
    fetchPolicy: 'cache-and-network',
  })

  const loadMoreEscapesInMarketScoped = useCallback(
    () =>
      loadMoreEscapesInMarket({
        variables: {
          listingId: listing.id,
          scope: 'market',
        },
      }),
    [loadMoreEscapesInMarket, listing.id],
  )

  const hasEnoughRegionalEscapes = useScopedEscapes({
    canFetchMoreRegional,
    regionalSuggestionsCount: regionalSuggestions?.length,
    loadMoreEscapesInMarket: loadMoreEscapesInMarketScoped,
  })

  const marketPageInfo = moreEscapesMarketData?.moreEscapes?.pageInfo

  const canFetchMoreMarket = marketPageInfo?.hasNextPage

  const marketSuggestions = moreEscapesMarketData?.moreEscapes?.edges?.map(
    ({ node }) => node.result,
  ) as Suggestion[]

  const handleFetchMoreMarket = useCallback(() => {
    if (moreEscapesMarketCalled && !moreEscapesMarketLoading) {
      fetchMoreMarket({
        variables: {
          cursor: marketPageInfo?.endCursor,
        },
      })
    }
  }, [
    fetchMoreMarket,
    moreEscapesMarketCalled,
    moreEscapesMarketLoading,
    marketPageInfo,
  ])

  // Prefetched: render once the seed is in the cache (data present on both the
  // server and first client render). Non-prefetched: keep the one-tick gate.
  if (prefetched) {
    if (!moreEscapesRegionData?.moreEscapes) return null
  } else if (!hydrated) {
    return null
  }

  const content = hasEnoughRegionalEscapes ? (
    <PaginatedSlides
      customTitle={
        customTitle || (
          <Title>
            More{' '}
            {listing?.state && listing?.region && (
              <a
                href={`/${listing.state.toLowerCase()}/${listing.region.slug}`}
              >
                {listing?.region_name}
              </a>
            )}{' '}
            Escapes
          </Title>
        )
      }
      called={moreEscapesRegionCalled}
      loading={moreEscapesRegionLoading}
      canFetchMore={canFetchMoreRegional}
      onFetchMore={handleFetchMoreRegional}
      items={regionalSuggestions}
      ItemComponent={SuggestionItem}
      minHeight=""
    />
  ) : (
    <PaginatedSlides
      customTitle={
        customTitle || <Title>More {listing?.market?.name} Escapes</Title>
      }
      called={moreEscapesMarketCalled}
      loading={moreEscapesMarketLoading}
      canFetchMore={canFetchMoreMarket}
      onFetchMore={handleFetchMoreMarket}
      items={marketSuggestions}
      ItemComponent={SuggestionItem}
      minHeight=""
    />
  )

  // Prefetched renders the seeded cards directly. Otherwise keep the
  // visibility wrapper that triggers the lazy fetch on scroll.
  return prefetched ? (
    content
  ) : (
    <TrackVisibility
      onChange={(isVisible) => {
        if (isVisible) setVisible(true)
      }}
    >
      {content}
    </TrackVisibility>
  )
}

export default Suggestions
